import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2, ChevronLeft } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/core/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-between gap-3 whitespace-nowrap rounded-full text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary-900 text-primary-0 hover:bg-primary-800",
        secondary: "bg-primary-100 text-primary-900 hover:bg-primary-100/80",
        outline:
          "border border-input bg-primary-0 hover:bg-primary-100 text-primary-900",
        ghost:
          "shadow-none bg-primary-0 text-primary-900 hover:bg-primary-100 hover:text-primary-900",
        transparent:
          "bg-transparent text-primary-900 hover:bg-primary-100/50 hover:text-primary-900",
        transparentWhite:
          "bg-transparent border-1 text-white hover:bg-white/10 hover:text-white border-primary-0",
      },
      size: {
        sm: "w-fit h-9 p-2 ps-4 text-sm",
        default: "w-fit h-9 p-1 ps-4 text-sm",
        lg: "w-fit h-11 text-base ps-4 p-2",
        full: "h-11 text-base p-2 ps-4 w-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function circleColors(variant: string | null) {
  const v = variant ?? "default";
  const darkVariants = ["black", "default", "ghost"];
  if (darkVariants.includes(v)) {
    return { bg: "bg-white", text: "text-black" };
  }
  return { bg: "bg-black", text: "text-white" };
}

type CustomButtonProps = {
  loading?: boolean;
  children: ReactNode;
  asChild?: boolean;
  circleIcon?: ReactNode;
} & VariantProps<typeof buttonVariants> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size">;

export default function CustomButton({
  loading = false,
  children,
  className,
  variant = "default",
  size = "default",
  disabled,
  type = "button",
  asChild = false,
  circleIcon,
  ...props
}: CustomButtonProps) {
  const Comp = asChild ? Slot : Button;
  const circle = circleColors(variant ?? "default");

  return (
    <Comp
      type={type}
      disabled={loading || disabled}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      <div className="flex items-center justify-between gap-3">
        <span>{children}</span>
        <span
          className={cn(
            "flex items-center justify-center size-7.5 rounded-full",
            circle.bg
          )}
        >
          {loading ? (
            <Loader2 className={cn("animate-spin size-4", circle.text)} />
          ) : (
            circleIcon || (
              <ChevronLeft className={cn("size-5", circle.text)} />
            )
          )}
        </span>
      </div>
    </Comp>
  );
}
